import { CheckCircle2, Mail, MapPin } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { useAuth } from '../lib/auth';
import { formatUnitLabel, ownerUnits, pb } from '../lib/pb';
import { usePageMeta } from '../lib/usePageMeta';

const RECORDS_TOPICS = [
  { value: 'minutes', label: 'Meeting minutes' },
  { value: 'budget', label: 'Budget / financial statements' },
  { value: 'reserve-study', label: 'Reserve study' },
  { value: 'sirs', label: 'SIRS report' },
  { value: 'wind-mitigation', label: 'Wind mitigation report' },
  { value: 'insurance-policy', label: 'Insurance policy document' },
  { value: 'contracts', label: 'Vendor contracts / bids' },
  { value: 'governing-docs', label: 'Governing documents' },
  { value: 'other', label: 'Other (describe below)' },
] as const;
type RecordsTopic = (typeof RECORDS_TOPICS)[number]['value'];

const BOARD_EMAIL = 'sunsetharbor3873@yahoo.com';

const CATEGORIES = [
  { value: 'general-inquiry', label: 'General inquiry' },
  { value: 'records-request', label: 'Records request' },
  { value: 'maintenance-issue', label: 'Maintenance issue' },
  { value: 'real-estate', label: 'Real estate inquiry' },
  { value: 'other', label: 'Other' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

function isValidCategory(value: string | null): value is CategoryValue {
  return CATEGORIES.some((c) => c.value === value);
}

const BUILDING_LAT = 28.3548298;
const BUILDING_LON = -80.6116973;
const MAP_BBOX = `${BUILDING_LON - 0.005}%2C${BUILDING_LAT - 0.005}%2C${
  BUILDING_LON + 0.005
}%2C${BUILDING_LAT + 0.005}`;
const MAP_URL = `https://www.openstreetmap.org/export/embed.html?bbox=${MAP_BBOX}&layer=mapnik&marker=${BUILDING_LAT}%2C${BUILDING_LON}`;
const MAP_LINK = `https://www.openstreetmap.org/?mlat=${BUILDING_LAT}&mlon=${BUILDING_LON}#map=17/${BUILDING_LAT}/${BUILDING_LON}`;

export function Contact(): JSX.Element {
  const [searchParams] = useSearchParams();
  const initialCategory: CategoryValue = isValidCategory(searchParams.get('category'))
    ? (searchParams.get('category') as CategoryValue)
    : 'general-inquiry';
  const topicFromQuery = searchParams.get('topic');

  const { isAuthenticated, user } = useAuth();
  const [category, setCategory] = useState<CategoryValue>(initialCategory);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [unit, setUnit] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recordsTopics, setRecordsTopics] = useState<RecordsTopic[]>(() =>
    RECORDS_TOPICS.some((t) => t.value === topicFromQuery) ? [topicFromQuery as RecordsTopic] : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isRecordsRequest = category === 'records-request';

  usePageMeta();

  function toggleTopic(t: RecordsTopic): void {
    setRecordsTopics((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  async function submitRecordsRequest(): Promise<void> {
    if (!isAuthenticated || !user) {
      setSubmitError('Please sign in before submitting a records request.');
      return;
    }
    if (recordsTopics.length === 0) {
      setSubmitError('Select at least one type of record being requested.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const topicLabels = recordsTopics.map(
        (t) => RECORDS_TOPICS.find((r) => r.value === t)?.label ?? t,
      );
      await pb.collection('records_requests').create({
        requester: user.id,
        categories: topicLabels,
        notes: message.trim(),
        status: 'open',
        opened_at: new Date().toISOString(),
      });
      setSubmitSuccess(true);
      setMessage('');
      setRecordsTopics([]);
    } catch (err) {
      console.error(err);
      setSubmitError('Could not submit the request. Please try again or contact the board.');
    } finally {
      setSubmitting(false);
    }
  }

  function submitMailto(): void {
    const categoryLabel = CATEGORIES.find((c) => c.value === category)?.label ?? 'General inquiry';
    const fullSubject = `[${categoryLabel}] ${subject.trim() || '(no subject)'}`;
    const bodyLines = [
      `From: ${name || '(no name)'} <${email || 'no-email'}>`,
      unit ? `Unit: ${unit}` : 'Unit: (not an owner / not provided)',
      `Category: ${categoryLabel}`,
      '',
      message,
    ];
    const mailto = `mailto:${BOARD_EMAIL}?subject=${encodeURIComponent(
      fullSubject,
    )}&body=${encodeURIComponent(bodyLines.join('\r\n'))}`;
    window.location.href = mailto;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (isRecordsRequest) {
      void submitRecordsRequest();
    } else {
      submitMailto();
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Reach the Board"
        title="Contact Sunset Harbor"
        lead="The fastest way to reach the board is through the form below. For property emergencies, dial 911 first."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                Contact information
              </p>
              <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
                3873 South Banana River Boulevard
              </h2>

              <ul className="mt-8 space-y-5">
                <li className="flex items-start gap-4">
                  <span
                    className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <MapPin size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                      Mailing address
                    </p>
                    <p className="mt-1 text-base text-brand-body-dark">
                      3873 South Banana River Boulevard, #5000
                      <br />
                      Cocoa Beach, FL 32931
                    </p>
                    <p className="mt-2 text-sm text-brand-body-medium">
                      Mailboxes are by the elevator on the third floor.
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span
                    className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <Mail size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                      Email
                    </p>
                    <p className="mt-1 text-base text-brand-body-dark">
                      <a
                        href={`mailto:${BOARD_EMAIL}`}
                        className="text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
                      >
                        {BOARD_EMAIL}
                      </a>
                    </p>
                  </div>
                </li>
              </ul>

              <div className="mt-10 overflow-hidden rounded-lg shadow-card">
                <iframe
                  title="Map showing 3873 South Banana River Boulevard, Cocoa Beach, Florida on OpenStreetMap"
                  src={MAP_URL}
                  loading="lazy"
                  className="block aspect-[4/3] w-full border-0"
                />
              </div>
              <p className="mt-2 text-xs text-brand-body-medium">
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-brand-yellow-deep/40 underline-offset-2 hover:text-brand-charcoal"
                >
                  View larger map on OpenStreetMap
                </a>
                {' · '}
                <span>Data © OpenStreetMap contributors</span>
              </p>
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                Send a message
              </p>
              <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
                We typically respond within 48 hours.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-brand-body-dark">
                The form opens your email program with the message ready to send to the board. For
                records requests, please pick the matching category so we can route it correctly
                under FS 718.111(12)(c)&apos;s ten-working-day rule.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate={false}>
                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-semibold text-brand-charcoal"
                  >
                    Category <span className="text-brand-yellow-deep">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value as CategoryValue);
                      setSubmitError(null);
                      setSubmitSuccess(false);
                    }}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Records-request UI: success state */}
                {isRecordsRequest && submitSuccess ? (
                  <div className="rounded-md border border-green-200 bg-green-50 p-5">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-600 text-white">
                        <CheckCircle2 size={14} aria-hidden="true" />
                      </span>
                      <div>
                        <p className="font-semibold text-green-900">Records request submitted.</p>
                        <p className="mt-1 text-sm text-green-800">
                          A board officer will respond within ten working days per Florida Statute
                          718.111(12)(c). You&rsquo;ll get an email when the request is fulfilled or
                          declined. Submit another request below if you need additional records.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Records-request UI: not authenticated */}
                {isRecordsRequest && !isAuthenticated ? (
                  <div className="rounded-md border border-brand-yellow bg-brand-yellow/10 p-5">
                    <p className="font-semibold text-brand-charcoal">Owner sign-in required.</p>
                    <p className="mt-2 text-sm leading-relaxed text-brand-body-dark">
                      Records requests are tracked against your owner account so the ten-working-day
                      clock starts immediately and the board can route the response.
                    </p>
                    <Link
                      to="/login"
                      className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep"
                    >
                      Sign in to continue
                    </Link>
                  </div>
                ) : null}

                {/* Records-request UI: authenticated. Topic checkboxes + notes */}
                {isRecordsRequest && isAuthenticated ? (
                  <>
                    <div className="rounded-md border border-brand-surface bg-brand-surface/40 p-4 text-sm text-brand-body-dark">
                      Signed in as <strong>{user?.name ?? user?.email}</strong>
                      {ownerUnits(user).length > 0 ? ` · ${formatUnitLabel(ownerUnits(user))}` : ''}.
                      Request will be logged against your account.
                    </div>

                    <fieldset>
                      <legend className="text-sm font-semibold text-brand-charcoal">
                        Which records? <span className="text-brand-yellow-deep">*</span>
                      </legend>
                      <p className="mt-1 text-xs text-brand-body-medium">Select all that apply.</p>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {RECORDS_TOPICS.map((t) => (
                          <label
                            key={t.value}
                            className="flex items-center gap-2 rounded border border-brand-surface bg-brand-white px-3 py-2 text-sm text-brand-body-dark hover:bg-brand-surface/50"
                          >
                            <input
                              type="checkbox"
                              checked={recordsTopics.includes(t.value)}
                              onChange={() => toggleTopic(t.value)}
                              className="h-4 w-4 rounded border-brand-surface text-brand-yellow focus:ring-brand-yellow"
                            />
                            {t.label}
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <div>
                      <label
                        htmlFor="rr-notes"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Notes <span className="text-brand-body-medium">(optional)</span>
                      </label>
                      <textarea
                        id="rr-notes"
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Specific date ranges, intended use, etc."
                        className="mt-2 block w-full rounded-md border border-brand-surface bg-brand-white px-3 py-2 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    {submitError ? (
                      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {submitError}
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full sm:w-auto"
                        disabled={submitting}
                      >
                        {submitting ? 'Submitting...' : 'Submit records request'}
                      </Button>
                      <p className="text-xs text-brand-body-medium">
                        Tracked in the board admin queue. Statute window: 10 working days.
                      </p>
                    </div>
                  </>
                ) : null}

                {/* Non-records-request UI: standard mailto form */}
                {!isRecordsRequest ? (
                  <>
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Name <span className="text-brand-yellow-deep">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Email <span className="text-brand-yellow-deep">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="unit"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Unit number{' '}
                        <span className="text-brand-body-medium">(if you are an owner)</span>
                      </label>
                      <input
                        id="unit"
                        name="unit"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="e.g., 207"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Subject <span className="text-brand-yellow-deep">*</span>
                      </label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-semibold text-brand-charcoal"
                      >
                        Message <span className="text-brand-yellow-deep">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="mt-2 block w-full rounded-md border border-brand-surface bg-brand-white px-3 py-2 text-base text-brand-body-dark shadow-sm focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full sm:w-auto"
                      >
                        Open in email
                      </Button>
                      <p className="text-xs text-brand-body-medium">
                        Submitting opens your email program with the message prefilled.
                      </p>
                    </div>
                  </>
                ) : null}
              </form>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
